import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import {
  ArrowLeft,
  Printer,
  CheckCircle2,
  Clock,
  Save,
  Truck,
  PackageCheck,
  Paperclip,
  FileText,
  Trash2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

import { CURRENCIES } from "../data/catalog";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "dd MMM yyyy, HH:mm");
  } catch {
    return iso;
  }
};

const downloadBase64 = (name, type, b64) => {
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const blob = new Blob([bytes], { type: type || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Could not download file");
  }
};

const formatBytes = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  // Billing-editable state
  const [saleOrderNumber, setSaleOrderNumber] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [dispatchStatus, setDispatchStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      setSaleOrderNumber(data.sale_order_number || "");
      setInvoiceNumber(data.invoice_number || "");
      setDispatchStatus(data.dispatch_status || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load order");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleApprove = async () => {
    if (!window.confirm("Approve this order? Salesperson will not be able to change it further.")) return;
    setActing(true);
    try {
      const { data } = await api.patch(`/orders/${id}/approve`);
      setOrder(data);
      toast.success("Order approved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally {
      setActing(false);
    }
  };

  const handleSaveBilling = async () => {
    setActing(true);
    try {
      const { data } = await api.patch(`/orders/${id}/billing`, {
        sale_order_number: saleOrderNumber,
        invoice_number: invoiceNumber,
        dispatch_status: dispatchStatus || null,
      });
      setOrder(data);
      toast.success("Billing details saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setActing(false);
    }
  };

  const handlePrint = () => window.print();

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Permanently delete order ${order?.order_no}?\n\nThis cannot be undone.`
      )
    )
      return;
    setActing(true);
    try {
      await api.delete(`/orders/${id}`);
      toast.success(`Order ${order?.order_no} deleted`);
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      setActing(false);
    }
  };

  if (loading || !order) {
    return (
      <div className="text-center py-16 text-[#5b6b4a]" data-testid="order-detail-loading">
        Loading…
      </div>
    );
  }

  const currencyMeta =
    CURRENCIES.find((c) => c.code === (order.currency || "INR")) || CURRENCIES[0];
  const currencySymbol = currencyMeta.symbol;

  const isPending = order.status === "pending";
  const canApprove = user?.role === "coordinator" && isPending;
  const canEditBilling =
    (user?.role === "billing" || user?.role === "coordinator") &&
    order.status === "approved";
  const canDelete = user?.role === "coordinator" || user?.role === "billing";

  return (
    <div className="grain ui-sans pb-16" data-testid="order-detail-page">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 mt-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3 no-print">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            data-testid="back-button"
            className="border-[#c4b991] hover:bg-[#efe7cf]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={handlePrint}
              data-testid="print-button"
              className="border-[#c4b991] hover:bg-[#efe7cf]"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            {canApprove && (
              <Button
                onClick={handleApprove}
                disabled={acting}
                data-testid="approve-button"
                className="bg-[#1d3a14] hover:bg-[#264a1a] text-[#faf6ea]"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Approve Order
              </Button>
            )}
            {canDelete && (
              <Button
                onClick={handleDelete}
                disabled={acting}
                data-testid="delete-order-button"
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Order
              </Button>
            )}
          </div>
        </div>

        <div className="print-card bg-white/85 border border-[#d6cdb4] rounded-xl p-6 shadow-sm">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#5b6b4a]">
                Order Number
              </div>
              <div className="brand-serif text-3xl font-bold text-[#1d3a14]">
                {order.order_no}
              </div>
              <div className="text-sm text-[#5b6b4a] mt-1">
                Placed {formatDate(order.created_at)} · Salesperson:{" "}
                <span className="font-medium text-[#1f2a1a]">
                  {order.salesperson_name}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span
                className={cn(
                  "inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border",
                  isPending
                    ? "bg-[#c47b28]/15 text-[#8a4c0d] border-[#c47b28]/40"
                    : "bg-[#1d3a14]/15 text-[#1d3a14] border-[#1d3a14]/40"
                )}
                data-testid="order-status"
              >
                {isPending ? <Clock className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                {isPending ? "Pending" : "Approved"}
              </span>
              {order.approved_at && (
                <span className="text-xs text-[#5b6b4a]">
                  Approved by {order.approved_by} · {formatDate(order.approved_at)}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="print-card bg-white/80 border border-[#d6cdb4] rounded-xl p-5">
            <h2 className="text-lg font-semibold text-[#1d3a14] mb-3">Customer</h2>
            <div className="space-y-1 text-sm">
              <div>
                <span className="text-[#5b6b4a]">Name: </span>
                <span className="font-medium">{order.customer?.name}</span>
              </div>
              <div>
                <span className="text-[#5b6b4a]">City: </span>
                <span>{order.customer?.city || "—"}</span>
              </div>
              <div>
                <span className="text-[#5b6b4a]">Pricelist: </span>
                <span>{order.pricelist || "—"}</span>
              </div>
              <div>
                <span className="text-[#5b6b4a]">Segment: </span>
                <span>{order.segment || "—"}</span>
              </div>
              {order.currency && order.currency !== "INR" && (
                <div>
                  <span className="text-[#5b6b4a]">Currency: </span>
                  <span className="font-mono">
                    {order.currency} · 1 {order.currency} = ₹
                    {order.conversion_rate_to_inr}
                  </span>
                </div>
              )}
              <div>
                <span className="text-[#5b6b4a]">Remarks: </span>
                <span>{order.remarks || "—"}</span>
              </div>
            </div>
          </div>

          <div className="print-card bg-white/80 border border-[#d6cdb4] rounded-xl p-5">
            <h2 className="text-lg font-semibold text-[#1d3a14] mb-3">
              Sales Coord / Billing
            </h2>
            {canEditBilling ? (
              <div className="space-y-3 text-sm">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                    Zoho Sale Order #
                  </Label>
                  <Input
                    data-testid="sale-order-number-input"
                    value={saleOrderNumber}
                    onChange={(e) => setSaleOrderNumber(e.target.value)}
                    placeholder="SO-00123"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                    Invoice #
                  </Label>
                  <Input
                    data-testid="invoice-number-input"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="INV-00123"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#5b6b4a]">
                    Dispatch Status
                  </Label>
                  <Select value={dispatchStatus || "none"} onValueChange={(v) => setDispatchStatus(v === "none" ? "" : v)}>
                    <SelectTrigger
                      className="mt-1 h-10"
                      data-testid="dispatch-status-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" data-testid="dispatch-none">
                        —
                      </SelectItem>
                      <SelectItem value="pending_dispatch" data-testid="dispatch-pending">
                        <Truck className="inline h-3 w-3 mr-1" /> Pending for Dispatch
                      </SelectItem>
                      <SelectItem value="despatched" data-testid="dispatch-despatched">
                        <PackageCheck className="inline h-3 w-3 mr-1" /> Despatched
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleSaveBilling}
                  disabled={acting}
                  data-testid="save-billing-button"
                  className="w-full bg-[#1d3a14] hover:bg-[#264a1a] text-[#faf6ea]"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Billing Details
                </Button>
              </div>
            ) : (
              <div className="space-y-1 text-sm">
                <div>
                  <span className="text-[#5b6b4a]">Sale Order #: </span>
                  <span className="font-mono">
                    {order.sale_order_number || "—"}
                  </span>
                </div>
                <div>
                  <span className="text-[#5b6b4a]">Invoice #: </span>
                  <span className="font-mono">{order.invoice_number || "—"}</span>
                </div>
                <div>
                  <span className="text-[#5b6b4a]">Dispatch Status: </span>
                  <span>
                    {order.dispatch_status === "pending_dispatch"
                      ? "Pending for Dispatch"
                      : order.dispatch_status === "despatched"
                      ? "Despatched"
                      : "—"}
                  </span>
                </div>
                {!canEditBilling && order.status === "pending" && (user?.role === "billing" || user?.role === "coordinator") && (
                  <p className="text-xs text-[#5b6b4a] mt-2 italic">
                    Billing fields unlock after the coordinator approves this order.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="print-card bg-white/85 border border-[#d6cdb4] rounded-xl p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-[#1d3a14] mb-3">Line Items</h2>
          <div className="overflow-x-auto rounded-lg border border-[#e7dfc5]">
            <table className="w-full text-sm" data-testid="line-items-table">
              <thead className="bg-[#1d3a14] text-[#faf6ea]">
                <tr>
                  <th className="text-left px-3 py-2">Variety</th>
                  <th className="text-left px-3 py-2">SKU</th>
                  <th className="text-right px-3 py-2">Bags</th>
                  <th className="text-right px-3 py-2">Qty (Kg)</th>
                  <th className="text-right px-3 py-2">Rate (/Kg)</th>
                  <th className="text-right px-3 py-2">Bag Cost</th>
                  <th className="text-right px-3 py-2">Line Total</th>
                </tr>
              </thead>
              <tbody>
                {order.line_items?.map((li, idx) => {
                  const isLoose = li.sku_key === "loose";
                  const bags = isLoose
                    ? null
                    : li.bags != null
                    ? li.bags
                    : li.weight
                    ? li.qty / li.weight
                    : null;
                  const bagCost = isLoose
                    ? null
                    : li.bag_cost != null
                    ? li.bag_cost
                    : li.weight
                    ? li.weight * li.rate
                    : null;
                  return (
                    <tr
                      key={idx}
                      className={cn(
                        "border-t border-[#efe7cf]",
                        idx % 2 === 0 ? "bg-[#fbf8ec]" : "bg-white"
                      )}
                    >
                      <td className="px-3 py-2 font-medium">{li.variety}</td>
                      <td className="px-3 py-2 text-[#264a1a]">{li.sku_label}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {bags == null ? "—" : bags.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {li.qty.toLocaleString("en-IN")} Kg
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {currencySymbol}{li.rate.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {bagCost == null ? "—" : `${currencySymbol}${bagCost.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-[#1d3a14]">
                        {currencySymbol}{li.line_total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-[#1d3a14] text-[#faf6ea]">
                  <td colSpan={6} className="px-3 py-2 text-right brand-serif text-lg">
                    Grand Total
                  </td>
                  <td
                    className="px-3 py-2 text-right brand-serif text-xl font-bold"
                    data-testid="detail-grand-total"
                  >
                    {currencySymbol}{(order.subtotal || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                  </td>
                </tr>
                {order.currency && order.currency !== "INR" && order.subtotal_inr && (
                  <tr className="bg-[#264a1a]/80 text-[#faf6ea]">
                    <td colSpan={4} className="px-3 py-1 text-right text-xs">
                      INR equivalent (1 {order.currency} = ₹{order.conversion_rate_to_inr})
                    </td>
                    <td className="px-3 py-1 text-right font-mono text-sm">
                      ₹{(order.subtotal_inr || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        </div>

        {order.attachments?.length > 0 && (
          <div className="print-card bg-white/80 border border-[#d6cdb4] rounded-xl p-5 no-print">
            <h2 className="text-lg font-semibold text-[#1d3a14] mb-3 flex items-center gap-2">
              <Paperclip className="h-4 w-4" /> Attachments ({order.attachments.length})
            </h2>
            <ul className="space-y-2">
              {order.attachments.map((a, idx) => (
                <li
                  key={idx}
                  className="flex items-center justify-between gap-3 bg-[#fbf8ec] border border-[#e7dfc5] rounded-lg px-3 py-2"
                  data-testid={`attachment-${idx}`}
                >
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <FileText className="h-4 w-4 shrink-0 text-[#264a1a]" />
                    <span className="truncate font-medium">{a.name}</span>
                    <span className="text-xs text-[#5b6b4a]">
                      ({formatBytes(a.size)})
                    </span>
                  </div>
                  {a.data_base64 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadBase64(a.name, a.type, a.data_base64)}
                      data-testid={`download-attachment-${idx}`}
                      className="border-[#c4b991] hover:bg-[#efe7cf]"
                    >
                      Download
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
}
